import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Select, Typography, Table, Tag, Space, Button, Avatar, Empty, Badge, Tooltip,
  InputNumber, Statistic,
} from 'antd';
import {
  TeamOutlined, ReloadOutlined, ShareAltOutlined, ApiOutlined, RightOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getEvents, getCoPresence, getNetworkingScore } from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

const PRIMARY = '#0F1D6B';
const TEAL    = '#00838F';

const fmtMins = (m) => {
  if (!m || m < 1) return '< 1m';
  if (m < 60) return `${m}m`;
  return `${Math.floor(m/60)}h ${m%60}m`;
};

export default function NetworkingInsights() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(null);
  const [minMinutes, setMinMinutes] = useState(5);
  const [pairs, setPairs] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEvents().then(r => {
      const ev = r.data.data || [];
      setEvents(ev);
      const a = ev.find(e => e.status === 'active') || ev[0];
      if (a) setEventId(a.id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!eventId) return;
    setLoading(true);
    Promise.all([
      getCoPresence({ event_id: eventId, min_minutes: minMinutes, limit: 100 }),
      getNetworkingScore({ event_id: eventId, limit: 20 }),
    ])
      .then(([p, s]) => { setPairs(p.data.data || []); setScores(s.data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventId, minMinutes]);

  useEffect(() => { load(); }, [load]);

  const totalConnections = pairs.length;
  const totalMinutes = pairs.reduce((sum, p) => sum + (p.shared_minutes || 0), 0);
  const uniqueDelegates = new Set([...pairs.map(p => p.d1_id), ...pairs.map(p => p.d2_id)]).size;

  return (
    <div>
      <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, color: PRIMARY }}>
            <ShareAltOutlined style={{ marginRight: 8, color: TEAL }} />Networking Insights
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Discover who met whom — based on RFID co-presence in the same zone at the same time
          </Text>
        </Col>
        <Col>
          <Space>
            <Tooltip title="Minimum minutes shared in same zone to count as a connection">
              <InputNumber min={1} max={60} value={minMinutes} onChange={v => setMinMinutes(v || 5)}
                addonBefore="Min" addonAfter="min" style={{ width: 130 }} />
            </Tooltip>
            <Select value={eventId} onChange={setEventId} style={{ width: 220 }} placeholder="Event">
              {events.map(e => (
                <Option key={e.id} value={e.id}>
                  <Badge color={e.status === 'active' ? 'green' : 'default'} text={e.event_name} />
                </Option>
              ))}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        </Col>
      </Row>

      {/* KPIs */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${TEAL}` }}>
            <Statistic title="Connections Found" value={totalConnections} prefix={<ApiOutlined />} valueStyle={{ color: TEAL }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid ${PRIMARY}` }}>
            <Statistic title="Delegates Connected" value={uniqueDelegates} prefix={<TeamOutlined />} valueStyle={{ color: PRIMARY }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid #f59e0b` }}>
            <Statistic title="Total Co-presence" value={fmtMins(totalMinutes)}
              prefix={<ClockCircleOutlined />} valueStyle={{ color: '#f59e0b', fontSize: 22 }} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" style={{ borderRadius: 10, borderTop: `3px solid #10b981` }}>
            <Statistic title="Avg Connection" value={pairs.length ? fmtMins(Math.round(totalMinutes / pairs.length)) : '0m'}
              valueStyle={{ color: '#10b981', fontSize: 22 }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Top connectors */}
        <Col xs={24} lg={9}>
          <Card title={<Space><ShareAltOutlined style={{ color: TEAL }} /> Top Connectors</Space>}
            style={{ borderRadius: 10 }}
            extra={<Tooltip title="Delegates who shared time with the most other people"><span style={{ color: '#bbb' }}>ℹ</span></Tooltip>}>
            {scores.length === 0 ? <Empty description="No connections found yet" /> : (
              <Table
                dataSource={scores}
                rowKey="delegate_id"
                size="small"
                pagination={false}
                onRow={(r) => ({ onClick: () => navigate(`/delegate-journey/${r.delegate_id}?event_id=${eventId}`), style: { cursor: 'pointer' } })}
                columns={[
                  { title: '#', width: 40, render: (_, __, i) => <Text strong style={{ color: i < 3 ? '#fbbf24' : '#94a3b8' }}>{i+1}</Text> },
                  { title: 'Delegate', dataIndex: 'delegate_name', render: (v, r) => (
                    <Space>
                      <Avatar size={28} style={{ background: r.category_color || PRIMARY }}>
                        {(v || '?').charAt(0).toUpperCase()}
                      </Avatar>
                      <Space direction="vertical" size={0}>
                        <Text strong style={{ fontSize: 12 }}>{v}</Text>
                        {r.category && <Tag color={r.category_color} style={{ fontSize: 9 }}>{r.category}</Tag>}
                      </Space>
                    </Space>
                  )},
                  { title: 'Connections', dataIndex: 'unique_connections', width: 75, align: 'center',
                    render: v => <Tag color="cyan" style={{ fontWeight: 700 }}>{v}</Tag> },
                ]}
              />
            )}
          </Card>
        </Col>

        {/* Connection pairs */}
        <Col xs={24} lg={15}>
          <Card title={<Space><ApiOutlined style={{ color: PRIMARY }} /> Strongest Connections</Space>}
            style={{ borderRadius: 10 }}>
            {pairs.length === 0 ? <Empty description="No qualifying co-presence pairs found" /> : (
              <Table
                dataSource={pairs}
                rowKey={(r, i) => i}
                size="small"
                pagination={{ pageSize: 15 }}
                columns={[
                  {
                    title: 'Delegate A', dataIndex: 'd1_name', render: (v, r) => (
                      <Space size={6} style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/delegate-journey/${r.d1_id}?event_id=${eventId}`); }}>
                        <Avatar size={24} style={{ background: r.d1_color || PRIMARY }}>{v?.charAt(0)}</Avatar>
                        <Text strong style={{ fontSize: 12 }}>{v}</Text>
                      </Space>
                    )
                  },
                  { title: '↔', width: 24, align: 'center', render: () => <ApiOutlined style={{ color: TEAL }} /> },
                  {
                    title: 'Delegate B', dataIndex: 'd2_name', render: (v, r) => (
                      <Space size={6} style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); navigate(`/delegate-journey/${r.d2_id}?event_id=${eventId}`); }}>
                        <Avatar size={24} style={{ background: r.d2_color || PRIMARY }}>{v?.charAt(0)}</Avatar>
                        <Text strong style={{ fontSize: 12 }}>{v}</Text>
                      </Space>
                    )
                  },
                  {
                    title: 'Time Together', dataIndex: 'shared_minutes', width: 120, align: 'right',
                    render: v => <Tag color="orange" style={{ fontWeight: 700 }}>{fmtMins(v)}</Tag>,
                    sorter: (a, b) => a.shared_minutes - b.shared_minutes, defaultSortOrder: 'descend',
                  },
                  {
                    title: 'Where', dataIndex: 'locations', width: 200,
                    render: v => <Text type="secondary" style={{ fontSize: 11 }}>📍 {v}</Text>
                  },
                ]}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
